import PropTypes from 'prop-types';
import React, { Component } from 'react';
import MaskedInput from 'react-text-mask';
import classNames from 'classnames';
import moment from 'moment-timezone';

import { get, pad } from 'ctm-util-functions';
import { inputUIComponent } from 'ctm-css-themes';

import * as actions from '../actions';
import { getDisabledProp, getComponentStateSuffix, getRecordingMaskClass } from '../utils';

import styles from './date_input_set.css';

class DateInputSetWithInputs extends Component {

    constructor(props, context) {
        super(props, context);

        this.inputProps = {
            day: {
                placeholder: 'DD',
                tabArgs: () => [2, this.monthNode]
            },
            month: {
                placeholder: 'MM',
                tabArgs: () => [2, this.yearNode]
            },
            year: {
                placeholder: 'YYYY',
                tabArgs: () => [4, null]
            }
        };

        this.state = this.getInitialState();
    }

    // eslint-disable-next-line camelcase
    UNSAFE_componentWillReceiveProps = (nextProps) => {
        const { value } = this.state;
        const { schema: twoWayBinding } = this.props;
        // set state if only valid
        if (twoWayBinding && nextProps.value !== value && nextProps.value.match(new RegExp('^\\d{4}-\\d{2}-\\d{2}$')) != null) {
            this.setState(this.generateDateState(nextProps.value));
        }
    };

    /**
     * Get the initial state so it can be set in the constructor
     * @returns {Object} - The initial state for the component, including the selected date as well as day, month and year values
     */
    getInitialState = () => {
        const { value } = this.props;
        if (!value) {
            return {
                selectedDate: moment().tz('Australia/Brisbane'),
                day: '',
                month: '',
                year: ''
            };
        }
        this.elementTouched();

        return this.generateDateState(value);
    };

    generateDateState = (value) => {
        const selectedDate = moment(value, 'YYYY-MM-DD');
        return {
            selectedDate,
            day: pad(selectedDate.date(), 2),
            month: pad(selectedDate.month() + 1, 2),
            year: selectedDate.year().toString()
        };
    };

    /**
     * Returns true if the key is one of the allowed keys.
     * Note: Standard key values can be found in here:
     *       https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/key/Key_Values
     * @param {Object} event - The event to use get value of a key pressed by the user.
     * @returns {Boolean} - True if charCode is a number, backspace or delete
     * @private
     */
    _isAllowedKey = (event) => {
        const allowedKeys = ['Shift', 'Tab', 'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight'];
        const key = this.getEventKey(event);

        return allowedKeys.indexOf(key) !== -1;
    };

    /**
     * Get the event key.
     * @param {Object} event - The event to use get value of a key pressed by the user.
     * @returns {String} - The key value of the event.
     */
    getEventKey = (event) => {
        let key = '';
        if (event.key !== undefined && event.key !== 'Unidentified') {
            // Handle the event with KeyboardEvent.key and set the key value.
            key = event.key;
        } else if (event.keyIdentifier !== undefined) {
            // Handle the event with KeyboardEvent.keyIdentifier and set the key value.
            key = event.keyIdentifier;
        } else if (event.keyCode !== undefined) {
            // Handle the event with KeyboardEvent.keyCode and set the key value.
            key = event.keyCode;
        }
        return key;
    };

    /**
     * Set the Value for the main field in the form state.
     */
    setValue = () => {
        const { dispatch, schema } = this.props;
        const { year, month, day } = this.state;
        const { days = true } = get(this.props, 'schema.field.dateConfig', {});
        const date = `${year}-${pad(month, 2)}-${pad(days ? day : [31, year % 4 === 0 ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1], 2)}`;
        const sendTouch = year.length === 4 && month.length === 2 && day.length === 2;
        dispatch(
            actions.setFormValue(
                (schema.backendIdentifier || schema.identifier),
                date,
                schema.stateKey,
                sendTouch
            )
        );
    };

    /**
     * Dispatch the element touched action to indicate that the user has added values to this input
     */
    elementTouched = () => {
        const { dispatch, schema } = this.props;
        dispatch(
            actions.elementTouched(schema.identifier, schema.stateKey)
        );
    };

    /**
     * onMonthChange event handler.
     * @param  {Object} event - the event details.
     * @param  {Object} key - the key of state top change.
     */
    onInputChange = (event, key) => {
        this.setState({ [key]: event.target.value });
    };

    /**
     * Dispatch onChange Actions from schema for each input
     */
    onChangeActions = () => {
        const {
            schema: { stateKey, onChangeActions = [] }, dispatch
        } = this.props;
        if (Array.isArray(onChangeActions)) {
            onChangeActions.forEach((action) => {
                dispatch({
                    ...action,
                    stateKey
                });
            });
        }
    };

    /**
     * Tab to the nextInput if the current input has reached it's maximum number of characters
     * @param {Object} event - The keyUp event
     * @param {Number} maxLength - The maximum allowed length of the value string
     * @param {Node} nextInput - The ref to the next input to tab to
     * @returns {Boolean} Returns false if we want to prevent a tab.
     */
    handleTab = (event, maxLength, nextInput = null) => {
        // This stops a very fast typist (or Acceptance tests)
        // from entering a value in Day and being skipped over "month" to Year.
        // It works as when you are tabbed to the month input, it highlights it.
        // The highlighted value is then
        const target = event.target;
        const selectedValue = target.value.substring(target.selectionStart, target.selectionEnd);
        if (selectedValue === target.value) {
            return false;
        }

        // If the user has pressed one of the allowed keys, then don't try to auto tab.
        if (this._isAllowedKey(event)) {
            return true;
        }

        if (nextInput && nextInput.inputElement) {
            // eslint-disable-next-line no-param-reassign
            nextInput = nextInput.inputElement;
        }

        const input = event.target.value;
        if (input.length >= maxLength) {
            // eslint-disable-next-line no-param-reassign
            event.target.value = input.slice(0, maxLength);
            if (nextInput !== null) {
                nextInput.focus();
                nextInput.select();
            }
        }
        return true;
    };

    /**
     * Dispatch onChange Actions from schema for each input
     * @param {Object} event - The blur event
     * @param {String} id - The identifier for the state value
     */
    onBlurInput = (event, id) => {
        const callback = () => {
            this.setValue();
            this.onChangeActions();
        };
        this.padOnBlur(event, id, callback);
    };

    /**
     * Add padding to the target value and on state when the input is blurred, if padding is required.
     * @param {Object} event - The blur event
     * @param {String} id - The identifier for the state value
     * @param {Function} callback - the Callback for on Blur Actions
     */
    padOnBlur = (event, id, callback) => {
        if (!event.target.value || id === 'year') {
            callback();
            return;
        }
        // Add extra 0's for day and month
        this.setState({ [id]: pad(event.target.value, 2) }, callback);
    };

    /**
     * Get the input props for one of the three inputs used by this component - day, month or year
     * @param {String} inputType - The type of input to get the props for - day, month or year
     * @returns {Object} - The set of props for the input
     */
    getInputProps = (inputType) => {
        const { schema = {} } = this.props;
        // ui component
        const UIComponentPrefix = get(schema, 'field.UIComponent', 'input_medium');
        const variant = get(schema, 'field.variant', null);
        const UIComponent = `${UIComponentPrefix}${getComponentStateSuffix(this.props)}`;
        const componentClasses = classNames(inputUIComponent[UIComponent], styles[`variant_${variant}`], getRecordingMaskClass(schema));

        const baseProps = {
            guide: false,
            pattern: '[0-9]*',
            type: 'tel',
            className: componentClasses,
            ...getDisabledProp(this.props)
        };

        const id = `${schema.identifier}.${inputType}`;
        return {
            ...baseProps,
            mask: [/\d/, /\d/],
            ref: (node) => {
                this[`${inputType}Node`] = node;
            },
            key: id,
            'data-id': id,
            placeholder: this.inputProps[inputType].placeholder,
            // eslint-disable-next-line react/destructuring-assignment
            value: this.state[inputType] || '',
            onChange: (event) => this.onInputChange(event, inputType),
            onBlur: (event) => this.onBlurInput(event, inputType),
            onKeyUp: (event) => {
                event.persist();
                this.handleTab(event, ...this.inputProps[inputType].tabArgs());
            }
        };
    };

    render = () => {
        const { schema } = this.props;
        const { years = true, months = true, days = true } = get(this.props, 'schema.field.dateConfig', {});
        return (
            <div className={styles.row} key={`${schema.identifier}.date_input_set`}>
                {days && (
                    <div className={styles.day}>
                        <MaskedInput {...this.getInputProps('day')} />
                    </div>
                )}
                {months && (
                    <div className={styles.month}>
                        <MaskedInput {...this.getInputProps('month')} />
                    </div>
                )}
                {years && (
                    <div className={styles.year}>
                        <MaskedInput {...this.getInputProps('year')} mask={[/[1-2]/, /\d/, /\d/, /\d/]} />
                    </div>
                )}
            </div>
        );
    };

}

DateInputSetWithInputs.propTypes = {
    value: PropTypes.string,
    dispatch: PropTypes.func.isRequired,
    schema: PropTypes.object.isRequired
};

export { DateInputSetWithInputs };
